/**
 * Premiums of a contract (« Primes » tab — fiche 22702): a `DataList` with
 * the stable columns of `CONTRACT_PREMIUMS_COLUMNS` (`reference`,
 * `periodeDu`, `periodeAu`, `ttc`, `situation`), rows mapped by
 * `contractsService.fetchContractPremiums`. Overrides / publish / loading /
 * error / empty states are handled by the shared `DataTableCard`.
 *
 * NOT registered by itself — bound to `extranet.contractPremiumsCard` in
 * `./register.ts`.
 */

import { useMemo } from 'react';
import { Receipt, type LucideIcon } from 'lucide-react';

import type { LayoutOverrides } from '@components/shared/configurable';

import type { ContractPremium } from '../../api/contractsService';
import { DataTableCard } from '../shared/DataTableCard';
import { buildContractPremiumsTableConfig } from './contractPremiumsTableConfig';

export interface ContractPremiumsTableCardProps {
  /** Registered id of this usage (bound by `bindCardProps`). */
  componentId: string;
  /** Base title (overridable); omitted ⇒ no title row. */
  title?: string;
  icon?: LucideIcon;
  rows: readonly ContractPremium[];
  loading?: boolean;
  error?: string | null;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

export function ContractPremiumsTableCard({
  componentId,
  title,
  icon = Receipt,
  rows,
  loading = false,
  error = null,
  _layoutOverrides,
}: Readonly<ContractPremiumsTableCardProps>) {
  const config = useMemo(
    () => buildContractPremiumsTableConfig(componentId, rows),
    [componentId, rows],
  );

  return (
    <DataTableCard
      componentId={componentId}
      title={title}
      icon={icon}
      config={config}
      rowCount={rows.length}
      loading={loading}
      error={error}
      _layoutOverrides={_layoutOverrides}
    />
  );
}
